/**
 * DocumentsListPage — the workspace's Browse view: a folder tree and a
 * list/grid of a Holder's files. Rendered inside `VaultWorkspacePage`,
 * which supplies the storage strip and panel above it.
 *
 * Layout:
 *   - Uploader (drag-and-drop), or a note where the area takes no files
 *   - Search bar — semantic search across the corpus, folder-agnostic
 *     (billed per submit)
 *   - Left rail: folder tree (assembled client-side from the flat list)
 *               + virtual "All files" and "Trash" nodes at the bottom
 *   - Main panel: the selected folder's files, every file (All files), or
 *               soft-deleted rows (Trash)
 *
 * Protected folders (`is_protected = true` — the Arche Saves and My-Fias
 * roots and the platform folders under them) get a lock badge; we don't
 * expose create-inside/rename/move/delete on them. User-managed folders
 * support the full set: nested creation (the "New folder" controls target
 * whichever folder is selected), rename, delete-to-Trash, and drag-and-drop
 * re-parenting (`components/shared/folder-drag.tsx`).
 *
 * What each file offers comes from `useFileActions`; where a dropped file
 * may go, from `useDocumentPlacement`. Both are shared with the Desktop and
 * Search views, so the three can't disagree.
 *
 * Sensitivity gating is server-enforced — the list never includes
 * docs above the caller's tier — but the UI surfaces a sensitivity
 * badge on each visible row so members understand what their team
 * mates can / can't see.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Lock,
  FolderOpen,
  Folder as FolderIcon,
  Trash2,
  Files,
  Inbox,
  Plus,
  Pencil,
  X,
  AlertCircle,
} from 'lucide-react';
import type { VaultDocumentFolderDto } from '@/lib/api';
import type { HolderSwitcherOption } from '@/components/HolderSwitcher';
import {
  useCreateVaultDocumentFolder,
  useDeleteVaultDocumentFolder,
  useEnsureVaultSystemFolders,
  useMoveVaultDocumentFolder,
  useRenameVaultDocumentFolder,
  useVaultDocumentFolders,
  useVaultDocumentsList,
  useVaultDocumentsSearch,
} from '../hooks/useVaultDocuments';
import { useFileActions } from '../hooks/useFileActions';
import { FolderExposureBadge, useFolderExposures } from '../components/FolderExposureBadge';
import { useDocumentPlacement } from '../components/DocumentPlacement';
import { vaultAreaOfPath } from '@/lib/vault-areas';
import { DocumentUploader } from '../components/DocumentUploader';
import { DocumentsView, ViewModeToggle } from '../components/shared/DocumentsView';
import { useOpenFile } from '../hooks/useOpenFile';
import { DeleteFolderDialog } from '../components/shared/DeleteFolderDialog';
import { EmptyTrashDialog } from '../components/shared/TrashDialogs';
import {
  FolderDragContext,
  useFolderRowDnd,
  type FolderDragContextValue,
} from '../components/shared/folder-drag';
import { canMoveFolderInto, friendlyFolderError } from '../lib/folder-moves';
import { describeFolderLocation } from '../lib/locations';
import { SemanticSearchResults } from '../components/shared/SemanticSearchResults';

interface DocumentsListPageProps {
  holderId: string;
  /** Active Holder option from `useHolderSwitcher` — drives Holder-aware uploader defaults
   * (org → default 'confidential'; personal → hide sensitivity picker entirely). Optional
   * for backwards-compat with the arche-page route which doesn't need to thread it. */
  holder?: HolderSwitcherOption | null;
  /** Optional deep-link target. Canonical path like `/Arche Saves/AI Images` —
   * the page resolves it to a folderId from the already-loaded folders
   * query and seeds the initial selection. Falls back to the root view
   * if the path doesn't match any folder in this Holder (e.g. Org Holder
   * that hasn't materialized the system scaffold yet). */
  initialFolderPath?: string;
  /** When true, suppress the inner h1/intro so the embedding page (e.g. MyDataPage) can
   * supply its own chrome without two stacked headers. */
  embedded?: boolean;
  /** Shown at the top of the page — the workspace's storage usage, when the
   *  workspace toolbar has no room to show it. */
  storageSlot?: React.ReactNode;
  /** Where Browse is: `root`, a folder id, `trash`, or `all` (All files) —
   *  for the workspace's "this folder" (Fias AI's context). */
  onLocationChange?: (location: string) => void;
}

/** Sentinel for the virtual "everything at Holder root" node. */
const ROOT_SELECTION = '__root__';
/** Sentinel for the virtual Trash view (deleted_at IS NOT NULL). */
const TRASH_SELECTION = '__trash__';
/** Sentinel for the flat "All files" view — every file in every area and
 *  folder (file areas §4.9: no file the user pays for is unreachable). */
const ALL_FILES_SELECTION = '__all__';

type Selection =
  | typeof ROOT_SELECTION
  | typeof TRASH_SELECTION
  | typeof ALL_FILES_SELECTION
  | string; // string = folderId

/** The folder a selection browses; `null` = the Holder root. The virtual
 *  views browse no folder, so anything placed from them lands at the root. */
function folderIdOfSelection(selection: Selection): string | null {
  return selection === ROOT_SELECTION ||
    selection === TRASH_SELECTION ||
    selection === ALL_FILES_SELECTION
    ? null
    : selection;
}

export function DocumentsListPage({
  holderId,
  holder,
  initialFolderPath,
  embedded,
  storageSlot,
  onLocationChange,
}: DocumentsListPageProps) {
  const navigate = useNavigate();
  const [selection, setSelection] = useState<Selection>(ROOT_SELECTION);
  useEffect(() => {
    onLocationChange?.(
      selection === ROOT_SELECTION
        ? 'root'
        : selection === TRASH_SELECTION
          ? 'trash'
          : selection === ALL_FILES_SELECTION
            ? 'all'
            : selection,
    );
  }, [selection, onLocationChange]);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchSubmitted, setSearchSubmitted] = useState('');
  const [emptyTrashConfirmOpen, setEmptyTrashConfirmOpen] = useState(false);
  const opener = useOpenFile();
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('grid');
  // On narrow screens the two-panel layout (folder rail + files) is too
  // cramped to use; this toggle lets the user show only one at a time.
  // Above `md` both panels render side-by-side and the toggle is hidden.
  const [mobilePane, setMobilePane] = useState<'folders' | 'files'>('files');
  const folders = useVaultDocumentFolders(holderId);
  // Every way of putting a document into a folder — both drop targets and the
  // Move dialog — goes through ONE controller, so they all apply the same
  // file-area rules and explain a refusal the same way. See
  // `lib/placement-plan.ts`.
  const placement = useDocumentPlacement({ folders: folders.data });
  // What each file offers (Move, Give an arche access, Delete, …) — decided
  // once for every view of the workspace, from the file areas and the
  // person's role. It also owns the dialogs those actions open.
  const fileActions = useFileActions({
    holderId,
    holder,
    folders: folders.data,
    placement,
  });

  // Folder create/rename/delete UI state lives here rather than inside
  // FolderTree because the files pane also opens the create form (its
  // "New folder" button targets the folder currently being browsed).
  // The form itself still renders in the tree, at the destination.
  const [folderUi, setFolderUi] = useState<FolderMutationUi>(null);

  // In-flight folder drag + the banner for a rejected move. Both are
  // page-level because a folder can be dragged out of the tree and
  // dropped in the files pane, or vice versa.
  const [draggingFolderId, setDraggingFolderId] = useState<string | null>(null);
  const [folderMoveError, setFolderMoveError] = useState<string | null>(null);
  const moveFolder = useMoveVaultDocumentFolder();
  const moveFolderMutate = moveFolder.mutate;
  const folderList = folders.data;
  const folderDrag = useMemo<FolderDragContextValue>(() => {
    const byId = new Map((folderList ?? []).map((f) => [f.folderId, f]));
    return {
      draggingFolderId,
      startDrag: (folderId) => {
        setFolderMoveError(null);
        setDraggingFolderId(folderId);
      },
      endDrag: () => setDraggingFolderId(null),
      canDropInto: (targetFolderId) =>
        draggingFolderId !== null && canMoveFolderInto(byId, draggingFolderId, targetFolderId),
      dropInto: (targetFolderId, draggedFolderId) => {
        setDraggingFolderId(null);
        // Re-validate against the payload rather than trusting the
        // hover state the row was rendered with.
        if (!canMoveFolderInto(byId, draggedFolderId, targetFolderId)) return;
        setFolderMoveError(null);
        moveFolderMutate(
          { folderId: draggedFolderId, parentFolderId: targetFolderId },
          { onError: (err) => setFolderMoveError(friendlyFolderError(err, 'move')) },
        );
      },
    };
  }, [folderList, draggingFolderId, moveFolderMutate]);

  // Self-heal for legacy Holders provisioned before the folder substrate
  // shipped (commit e80e6677, 2026-05-18). Those Holders skipped
  // `ensureSystemFolders` at creation time and have zero rows in
  // `vault_document_folders`, leaving the tree with only "Documents"
  // + "Trash" + no `/My-Fias/...` scaffold. The recovery route is
  // idempotent, so firing it once per Holder per mount when the folders
  // query lands empty patches the legacy state without affecting
  // already-scaffolded Holders. New Holders are unaffected (their
  // creation transaction provisions the scaffold).
  const ensureSystemFolders = useEnsureVaultSystemFolders();
  const ensureSystemFoldersMutate = ensureSystemFolders.mutate;
  const ensureAttempted = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!holderId) return;
    if (folders.isLoading || folders.isError) return;
    if ((folders.data ?? []).length > 0) return;
    if (ensureAttempted.current.has(holderId)) return;
    ensureAttempted.current.add(holderId);
    ensureSystemFoldersMutate(holderId);
  }, [holderId, folders.isLoading, folders.isError, folders.data, ensureSystemFoldersMutate]);

  // Resolve `initialFolderPath` → folderId from the already-loaded folders
  // list and seed the selection. One-shot per (holderId, path) pair so a
  // user who deep-links in and then navigates the tree manually doesn't get
  // snapped back. Gracefully no-ops if the path doesn't match a folder in
  // this Holder — the page just stays on the root view.
  const folderPathApplied = useRef<string | null>(null);
  useEffect(() => {
    if (!initialFolderPath) return;
    if (!holderId) return;
    if (folders.isLoading || folders.isError) return;
    const key = `${holderId}::${initialFolderPath}`;
    if (folderPathApplied.current === key) return;
    const match = (folders.data ?? []).find((f) => f.path === initialFolderPath);
    if (!match) return;
    folderPathApplied.current = key;
    setSelection(match.folderId);
  }, [initialFolderPath, holderId, folders.isLoading, folders.isError, folders.data]);

  // Filter the document list based on selection.
  // - root      → folderId: null
  // - <vdf_*>   → folderId: <id>
  // - trash     → deletedOnly: true (folder-agnostic; trash is a view, not a folder)
  const listFilters = useMemo(() => {
    if (selection === TRASH_SELECTION) {
      return { deletedOnly: true, includeArchived: true };
    }
    if (selection === ROOT_SELECTION) {
      return { folderId: null, includeArchived };
    }
    if (selection === ALL_FILES_SELECTION) {
      // No folder filter: every file, in every area.
      return { includeArchived };
    }
    return { folderId: selection, includeArchived };
  }, [selection, includeArchived]);

  const list = useVaultDocumentsList(holderId, listFilters);
  const search = useVaultDocumentsSearch(
    holderId,
    { query: searchSubmitted, topK: 20 },
    { enabled: !!searchSubmitted },
  );

  return (
    <div className="flex flex-col gap-6 p-6">
      {!embedded && (
        <div className="flex flex-col gap-2">
          <h1 className="text-2xl font-semibold text-foreground">Documents</h1>
          <p className="text-sm text-muted-foreground">
            Upload, search, and manage documents for this workspace. Sensitivity controls who can
            see each doc; semantic search finds the right chunks across PDF, DOCX, and text files.
          </p>
        </div>
      )}

      {storageSlot && <div className="flex">{storageSlot}</div>}

      {selection !== TRASH_SELECTION &&
        (() => {
          const targetFolder =
            selection === ROOT_SELECTION
              ? null
              : (folders.data ?? []).find((f) => f.folderId === selection);
          const folderLabel = targetFolder ? (targetFolder.path ?? targetFolder.name) : 'Documents';
          const area = vaultAreaOfPath(targetFolder?.path ?? null);
          if (area !== 'documents') {
            return (
              <p className="rounded-md border border-dashed border-border px-3 py-2 text-sm text-muted-foreground">
                {area === 'arche-saves'
                  ? 'Arche Saves holds files your arches delivered. You can open, rename, delete or move them into your Documents, but you can’t add files here.'
                  : 'My-Fias holds the files your arches keep for themselves. You can look at them and delete them, but you can’t add files here.'}
              </p>
            );
          }
          return (
            <DocumentUploader
              holderId={holderId}
              defaultSensitivity={holder?.holderType === 'org' ? 'confidential' : 'standard'}
              hideSensitivityPicker={holder?.holderType === 'personal'}
              folderId={folderIdOfSelection(selection)}
              folderLabel={folderLabel}
            />
          );
        })()}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          setSearchSubmitted(searchInput.trim());
        }}
        className="flex items-center gap-2"
      >
        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search documents semantically — e.g. 'renewal terms', 'NDA scope'"
          className="flex-1 rounded border border-border bg-card px-3 py-2 text-sm"
          maxLength={2000}
        />
        <button
          type="submit"
          disabled={!searchInput.trim()}
          className="rounded bg-primary px-4 py-2 text-sm text-primary-foreground disabled:opacity-50"
        >
          Search
        </button>
        {searchSubmitted && (
          <button
            type="button"
            className="text-sm text-muted-foreground underline"
            onClick={() => {
              setSearchInput('');
              setSearchSubmitted('');
            }}
          >
            Clear
          </button>
        )}
      </form>

      {searchSubmitted ? (
        <SemanticSearchResults
          query={searchSubmitted}
          loading={search.isLoading}
          matches={search.data}
          onOpen={(documentId) => navigate(`/a/arche_vault_documents/${documentId}`)}
        />
      ) : (
        <FolderDragContext.Provider value={folderDrag}>
          {placement.ui}
          {fileActions.ui}
          {folderMoveError && (
            <div
              role="alert"
              className="flex items-start gap-2 rounded border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span className="flex-1">{folderMoveError}</span>
              <button
                type="button"
                onClick={() => setFolderMoveError(null)}
                aria-label="Dismiss"
                className="shrink-0 rounded p-0.5 hover:bg-destructive/10"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
          <div className="flex flex-col gap-3 md:flex-row md:gap-4">
            <div
              role="tablist"
              aria-label="Switch between folders and files"
              className="flex w-full rounded-md border border-border bg-card p-0.5 text-sm md:hidden"
            >
              <button
                type="button"
                role="tab"
                aria-selected={mobilePane === 'folders'}
                onClick={() => setMobilePane('folders')}
                className={
                  'flex-1 rounded px-3 py-1.5 transition-colors ' +
                  (mobilePane === 'folders'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground')
                }
              >
                Folders
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={mobilePane === 'files'}
                onClick={() => setMobilePane('files')}
                className={
                  'flex-1 rounded px-3 py-1.5 transition-colors ' +
                  (mobilePane === 'files'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground')
                }
              >
                Files
              </button>
            </div>

            <aside
              className={
                (mobilePane === 'folders' ? 'block' : 'hidden') +
                ' w-full md:block md:w-56 md:shrink-0'
              }
            >
              <FolderTree
                holderId={holderId}
                folders={folders.data ?? []}
                loading={folders.isLoading}
                selection={selection}
                ui={folderUi}
                setUi={setFolderUi}
                onSelect={(next) => {
                  setSelection(next);
                  // On mobile the folder rail is a full-screen pane;
                  // after the user picks one, swap to the files view so
                  // they can see the result of their selection.
                  setMobilePane('files');
                }}
                onDropDocument={(folderId, documentId) => {
                  if (selection === TRASH_SELECTION) return;
                  void placement.place(documentId, folderId);
                }}
              />
            </aside>

            <div
              className={
                (mobilePane === 'files' ? 'flex' : 'hidden') + ' flex-1 flex-col gap-3 md:flex'
              }
            >
              {(() => {
                if (selection === TRASH_SELECTION) {
                  return (
                    <div className="flex items-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-2 md:hidden">
                      <Trash2 className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="truncate text-sm font-medium text-foreground">Trash</span>
                    </div>
                  );
                }
                if (selection === ALL_FILES_SELECTION) {
                  return (
                    <div className="flex items-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-2">
                      <Files className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="truncate text-sm font-medium text-foreground">
                        All files
                      </span>
                      <span className="truncate text-xs text-muted-foreground">
                        — every file in this workspace, in every folder
                      </span>
                    </div>
                  );
                }
                const currentFolder =
                  selection !== ROOT_SELECTION
                    ? (folders.data ?? []).find((f) => f.folderId === selection)
                    : undefined;
                // Build breadcrumb segments from the folder's canonical path
                // (e.g. "/Arche Saves/AI Images" → ["My-Fias", "AI Images"]).
                // Each segment resolves back to a folder by its cumulative
                // path so the user can jump to any ancestor.
                const segments: string[] = currentFolder?.path?.split('/').filter(Boolean) ?? [];
                const allFolders = folders.data ?? [];
                const crumbs = segments.map((segment, i) => {
                  const cumulativePath = '/' + segments.slice(0, i + 1).join('/');
                  const folder = allFolders.find((f) => f.path === cumulativePath);
                  return {
                    label: segment,
                    folderId: folder?.folderId ?? null,
                    isLast: i === segments.length - 1,
                  };
                });
                return (
                  <nav
                    aria-label="Folder breadcrumbs"
                    className="flex items-center gap-1 overflow-x-auto rounded-md border border-border bg-muted/30 px-3 py-2 text-sm md:hidden"
                  >
                    <button
                      type="button"
                      onClick={() => setSelection(ROOT_SELECTION)}
                      disabled={selection === ROOT_SELECTION}
                      className={
                        'flex items-center gap-1.5 rounded px-1.5 py-0.5 shrink-0 ' +
                        (selection === ROOT_SELECTION
                          ? 'text-foreground font-medium cursor-default'
                          : 'text-muted-foreground hover:bg-muted hover:text-foreground')
                      }
                    >
                      <Inbox className="h-4 w-4" />
                      <span>Documents</span>
                    </button>
                    {crumbs.map((c, i) => (
                      <span key={i} className="flex items-center gap-1 shrink-0">
                        <span className="text-muted-foreground/60">/</span>
                        {c.isLast || !c.folderId ? (
                          <span className="rounded px-1.5 py-0.5 font-medium text-foreground">
                            {c.label}
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => c.folderId && setSelection(c.folderId)}
                            className="rounded px-1.5 py-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                          >
                            {c.label}
                          </button>
                        )}
                      </span>
                    ))}
                  </nav>
                );
              })()}
              <div className="flex items-center justify-between">
                {selection !== TRASH_SELECTION ? (
                  <label className="flex items-center gap-2 text-sm text-muted-foreground">
                    <input
                      type="checkbox"
                      checked={includeArchived}
                      onChange={(e) => setIncludeArchived(e.target.checked)}
                    />
                    Show archived
                  </label>
                ) : (list.data ?? []).length > 0 ? (
                  <p className="text-sm text-muted-foreground">
                    Items here are permanently deleted after 30 days.
                  </p>
                ) : (
                  <span />
                )}
                <div className="flex items-center gap-2">
                  {selection === TRASH_SELECTION && (list.data ?? []).length > 0 && (
                    <button
                      type="button"
                      onClick={() => setEmptyTrashConfirmOpen(true)}
                      className="rounded border border-destructive/40 bg-card px-3 py-1 text-sm text-destructive hover:bg-destructive/10"
                    >
                      Empty Trash
                    </button>
                  )}
                  {selection !== TRASH_SELECTION &&
                    (() => {
                      // Creates inside the folder currently being browsed.
                      // The name form itself renders in the folder rail at
                      // the destination, so on mobile — where the two panes
                      // are exclusive — we switch over to it.
                      const currentFolder =
                        selection === ROOT_SELECTION
                          ? undefined
                          : (folders.data ?? []).find((f) => f.folderId === selection);
                      const blocked = !!currentFolder?.isProtected;
                      return (
                        <button
                          type="button"
                          disabled={blocked}
                          title={
                            blocked
                              ? 'System folders cannot contain new folders.'
                              : currentFolder
                                ? `Create a folder inside "${currentFolder.name}"`
                                : 'Create a folder at the top level'
                          }
                          onClick={() => {
                            setFolderUi({
                              kind: 'create-under',
                              parentFolderId: currentFolder?.folderId ?? null,
                            });
                            setMobilePane('folders');
                          }}
                          className="flex items-center gap-1.5 rounded border border-border bg-card px-3 py-1 text-sm hover:bg-accent disabled:opacity-50"
                        >
                          <Plus className="h-3.5 w-3.5" />
                          New folder
                        </button>
                      );
                    })()}
                  <ViewModeToggle mode={viewMode} onChange={setViewMode} />
                </div>
              </div>
              <DocumentsView
                mode={viewMode}
                loading={list.isLoading}
                documents={list.data ?? []}
                folders={folders.data ?? []}
                currentFolderId={folderIdOfSelection(selection)}
                isTrash={selection === TRASH_SELECTION}
                onOpenDocument={opener.open}
                onOpenFolder={(folderId) => setSelection(folderId)}
                actionsFor={fileActions.actionsFor}
                locationOf={
                  selection === ALL_FILES_SELECTION
                    ? (doc) => describeFolderLocation(doc.folderId, folders.data ?? [])
                    : undefined
                }
                onDropOnFolder={(folderId, documentId) => {
                  if (selection === TRASH_SELECTION) return;
                  void placement.place(documentId, folderId);
                }}
              />
              {/* Server paging (file areas §4.9): every file stays reachable,
                  however many there are. */}
              {list.hasNextPage && (
                <div className="mt-3 flex items-center justify-center gap-3 text-xs text-muted-foreground">
                  <span>
                    Showing {(list.data ?? []).length} of {list.total}
                  </span>
                  <button
                    type="button"
                    className="rounded border border-border px-3 py-1 text-sm text-foreground hover:bg-accent disabled:opacity-50"
                    disabled={list.isFetchingNextPage}
                    onClick={() => void list.fetchNextPage()}
                  >
                    {list.isFetchingNextPage ? 'Loading…' : 'Load more'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </FolderDragContext.Provider>
      )}

      <EmptyTrashDialog
        open={emptyTrashConfirmOpen}
        holderId={holderId}
        trashCount={selection === TRASH_SELECTION ? list.total : 0}
        onClose={() => setEmptyTrashConfirmOpen(false)}
      />

      {opener.ui}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Folder tree (left rail)
// ---------------------------------------------------------------------------

interface FolderTreeProps {
  holderId: string;
  folders: VaultDocumentFolderDto[];
  loading: boolean;
  selection: Selection;
  /** Owned by the page — the files pane opens the create form too. */
  ui: FolderMutationUi;
  setUi: (next: FolderMutationUi) => void;
  onSelect: (selection: Selection) => void;
  /** Drop a dragged document onto a folder row (or "Documents" =
   * Holder root). The dataTransfer payload is the documentId. Trash
   * row intentionally has no drop handler — soft-deleting via drag
   * would be a footgun. */
  onDropDocument?: (folderId: string | null, documentId: string) => void;
}

interface TreeNode {
  folder: VaultDocumentFolderDto;
  children: TreeNode[];
}

/** Assemble the flat folder list into a parent-rooted tree. */
function buildTree(folders: VaultDocumentFolderDto[]): TreeNode[] {
  const byId = new Map<string, TreeNode>();
  for (const f of folders) byId.set(f.folderId, { folder: f, children: [] });

  const roots: TreeNode[] = [];
  for (const node of byId.values()) {
    if (node.folder.parentFolderId && byId.has(node.folder.parentFolderId)) {
      byId.get(node.folder.parentFolderId)!.children.push(node);
    } else {
      roots.push(node);
    }
  }

  const byName = (a: TreeNode, b: TreeNode) =>
    a.folder.name.localeCompare(b.folder.name, undefined, { sensitivity: 'base' });
  const sortRec = (nodes: TreeNode[]) => {
    nodes.sort(byName);
    for (const n of nodes) sortRec(n.children);
  };
  sortRec(roots);
  return roots;
}

/** UI state for an in-progress mutation against a specific folder.
 * `'create-under'` carries `null` (= Holder root) or a parent folder id;
 * `'rename'` and `'delete'` carry a `vdf_*` id. Exactly one slot is
 * non-null at a time so the row visuals don't double up. */
type FolderMutationUi =
  | { kind: 'create-under'; parentFolderId: string | null }
  | { kind: 'rename'; folderId: string }
  | { kind: 'delete'; folderId: string }
  | null;

function FolderTree({
  holderId,
  folders,
  loading,
  selection,
  ui,
  setUi,
  onSelect,
  onDropDocument,
}: FolderTreeProps) {
  const tree = useMemo(() => buildTree(folders), [folders]);
  // One request for the whole holder, not one per folder row. Silent on
  // failure — this is an advisory annotation, and the access control it
  // describes lives on the serving path, not here.
  const exposures = useFolderExposures(holderId);
  const create = useCreateVaultDocumentFolder();
  const rename = useRenameVaultDocumentFolder();
  const remove = useDeleteVaultDocumentFolder();
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const closeUi = () => {
    setUi(null);
    setErrorMsg(null);
  };

  // Use the mutation's own onSuccess/onError callbacks rather than
  // awaiting mutateAsync — that path lets React Query re-render with
  // the post-mutation state before the awaiter resumes, which can
  // leave the inline form mounted for an extra frame and (in some
  // timing windows) appear to not close at all. Attaching the close
  // to the mutation's success path runs it inside the same status
  // transition, with no async gap.
  const handleCreate = (parentFolderId: string | null, name: string) => {
    setErrorMsg(null);
    create.mutate(
      { holderId, parentFolderId, name },
      {
        onSuccess: () => closeUi(),
        onError: (err) => setErrorMsg(friendlyFolderError(err, 'create')),
      },
    );
  };

  const handleRename = (folderId: string, name: string) => {
    setErrorMsg(null);
    rename.mutate(
      { folderId, name },
      {
        onSuccess: () => closeUi(),
        onError: (err) => setErrorMsg(friendlyFolderError(err, 'rename')),
      },
    );
  };

  const handleDelete = (folderId: string, opts?: { force?: boolean }) => {
    setErrorMsg(null);
    remove.mutate(
      { folderId, holderId, force: opts?.force },
      {
        onSuccess: () => {
          // If the deleted folder is currently selected, fall back to
          // "Documents" so the right pane isn't filtering on a
          // vanished id.
          if (selection === folderId) onSelect(ROOT_SELECTION);
          closeUi();
        },
        onError: (err) => setErrorMsg(friendlyFolderError(err, 'delete')),
      },
    );
  };

  // "New folder" nests into whatever folder the user is currently in.
  // A protected folder can't hold user folders — the resulting path
  // would land in the system /My-Fias namespace and the ops layer
  // rejects it — so those fall back to the Holder root. The label
  // always names the real destination, so the fallback is visible
  // rather than surprising.
  const selectedFolder =
    selection === ROOT_SELECTION || selection === TRASH_SELECTION
      ? undefined
      : folders.find((f) => f.folderId === selection);
  const newFolderParent =
    selectedFolder && !selectedFolder.isProtected ? selectedFolder : undefined;
  const creating = ui?.kind === 'create-under';
  const creatingAtRoot = creating && ui.parentFolderId === null;

  return (
    <nav
      aria-label="Folders"
      className="flex flex-col gap-1 rounded border border-border bg-card p-2 text-sm"
    >
      <TreeRow
        icon={<Inbox className="h-4 w-4" />}
        label="Documents"
        selected={selection === ROOT_SELECTION}
        onClick={() => onSelect(ROOT_SELECTION)}
        depth={0}
        rootDropTarget
        onDropDocument={onDropDocument ? (id) => onDropDocument(null, id) : undefined}
      />

      {!creating && (
        <button
          type="button"
          onClick={() => {
            setErrorMsg(null);
            setUi({ kind: 'create-under', parentFolderId: newFolderParent?.folderId ?? null });
          }}
          className="flex items-center gap-1.5 rounded px-2 py-1 text-left text-xs text-muted-foreground hover:bg-muted/40"
        >
          <Plus className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">
            {newFolderParent ? `New folder in "${newFolderParent.name}"` : 'New folder'}
          </span>
        </button>
      )}
      {creatingAtRoot && (
        <InlineFolderNameForm
          placeholder="Folder name"
          busy={create.isPending}
          onSubmit={(name) => handleCreate(null, name)}
          onCancel={closeUi}
          depth={0}
        />
      )}

      {loading && <p className="px-2 py-1 text-xs text-muted-foreground">Loading folders…</p>}

      {tree.map((node) => (
        <FolderNode
          key={node.folder.folderId}
          node={node}
          selection={selection}
          onSelect={onSelect}
          depth={0}
          ui={ui}
          setUi={setUi}
          onCreate={handleCreate}
          onRename={handleRename}
          onDelete={handleDelete}
          createPending={create.isPending}
          renamePending={rename.isPending}
          deletePending={remove.isPending}
          deleteError={errorMsg}
          onDropDocument={onDropDocument}
          exposures={exposures}
        />
      ))}

      {errorMsg && (
        <p className="mt-1 rounded bg-destructive/10 px-2 py-1 text-xs text-destructive">
          {errorMsg}
        </p>
      )}

      <div className="my-1 border-t border-border" />

      <TreeRow
        icon={<Files className="h-4 w-4" />}
        label="All files"
        selected={selection === ALL_FILES_SELECTION}
        onClick={() => onSelect(ALL_FILES_SELECTION)}
        depth={0}
      />
      <TreeRow
        icon={<Trash2 className="h-4 w-4" />}
        label="Trash"
        selected={selection === TRASH_SELECTION}
        onClick={() => onSelect(TRASH_SELECTION)}
        depth={0}
      />
    </nav>
  );
}

interface FolderNodeProps {
  node: TreeNode;
  selection: Selection;
  onSelect: (selection: Selection) => void;
  depth: number;
  ui: FolderMutationUi;
  setUi: (next: FolderMutationUi) => void;
  onCreate: (parentFolderId: string | null, name: string) => void;
  onRename: (folderId: string, name: string) => void;
  onDelete: (folderId: string, opts?: { force?: boolean }) => void;
  createPending: boolean;
  renamePending: boolean;
  deletePending: boolean;
  deleteError: string | null;
  onDropDocument?: (folderId: string | null, documentId: string) => void;
  /** Sparse Fias Transfer exposure map, fetched once by FolderTree. */
  exposures?: Record<string, number>;
}

function FolderNode({
  node,
  selection,
  onSelect,
  depth,
  exposures,
  ui,
  setUi,
  onCreate,
  onRename,
  onDelete,
  createPending,
  renamePending,
  deletePending,
  deleteError,
  onDropDocument,
}: FolderNodeProps) {
  const selected = selection === node.folder.folderId;
  const Icon = selected ? FolderOpen : FolderIcon;
  const userManaged = !node.folder.isProtected;
  const isRenaming = ui?.kind === 'rename' && ui.folderId === node.folder.folderId;
  const isDeleting = ui?.kind === 'delete' && ui.folderId === node.folder.folderId;
  const isCreatingChild = ui?.kind === 'create-under' && ui.parentFolderId === node.folder.folderId;

  return (
    <>
      {isRenaming ? (
        <InlineFolderNameForm
          initial={node.folder.name}
          busy={renamePending}
          onSubmit={(name) => onRename(node.folder.folderId, name)}
          onCancel={() => setUi(null)}
          depth={depth}
        />
      ) : (
        <TreeRow
          icon={<Icon className="h-4 w-4" />}
          label={node.folder.name}
          folder={node.folder}
          exposures={exposures}
          protectedFolder={node.folder.isProtected}
          selected={selected}
          onClick={() => onSelect(node.folder.folderId)}
          depth={depth}
          onDropDocument={
            onDropDocument ? (id) => onDropDocument(node.folder.folderId, id) : undefined
          }
          actions={
            userManaged && !isDeleting ? (
              <FolderRowActions
                onNewSubfolder={() =>
                  setUi({ kind: 'create-under', parentFolderId: node.folder.folderId })
                }
                onRename={() => setUi({ kind: 'rename', folderId: node.folder.folderId })}
                onDelete={() => setUi({ kind: 'delete', folderId: node.folder.folderId })}
              />
            ) : null
          }
        />
      )}

      <DeleteFolderDialog
        open={isDeleting}
        folderName={node.folder.name}
        deletePending={deletePending}
        deleteError={deleteError}
        onCancel={() => setUi(null)}
        onDelete={(opts) => onDelete(node.folder.folderId, opts)}
      />

      {isCreatingChild && (
        <InlineFolderNameForm
          placeholder="Subfolder name"
          busy={createPending}
          onSubmit={(name) => onCreate(node.folder.folderId, name)}
          onCancel={() => setUi(null)}
          depth={depth + 1}
        />
      )}

      {node.children.map((child) => (
        <FolderNode
          key={child.folder.folderId}
          node={child}
          selection={selection}
          onSelect={onSelect}
          depth={depth + 1}
          exposures={exposures}
          ui={ui}
          setUi={setUi}
          onCreate={onCreate}
          onRename={onRename}
          onDelete={onDelete}
          createPending={createPending}
          renamePending={renamePending}
          deletePending={deletePending}
          deleteError={deleteError}
          onDropDocument={onDropDocument}
        />
      ))}
    </>
  );
}

interface FolderRowActionsProps {
  onNewSubfolder: () => void;
  onRename: () => void;
  onDelete: () => void;
}

function FolderRowActions({ onNewSubfolder, onRename, onDelete }: FolderRowActionsProps) {
  const click = (handler: () => void) => (e: React.MouseEvent) => {
    // Don't fire the parent TreeRow's onClick (would toggle selection)
    // when the user is reaching for an action button.
    e.stopPropagation();
    handler();
  };
  const btn =
    'rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors';
  return (
    <div className="ml-auto flex items-center gap-0.5">
      <button
        type="button"
        aria-label="New subfolder"
        onClick={click(onNewSubfolder)}
        className={btn}
      >
        <Plus className="h-3 w-3" />
      </button>
      <button type="button" aria-label="Rename folder" onClick={click(onRename)} className={btn}>
        <Pencil className="h-3 w-3" />
      </button>
      <button type="button" aria-label="Delete folder" onClick={click(onDelete)} className={btn}>
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}

interface InlineFolderNameFormProps {
  initial?: string;
  placeholder?: string;
  busy: boolean;
  onSubmit: (name: string) => void;
  onCancel: () => void;
  depth: number;
}

function InlineFolderNameForm({
  initial,
  placeholder,
  busy,
  onSubmit,
  onCancel,
  depth,
}: InlineFolderNameFormProps) {
  const [value, setValue] = useState(initial ?? '');
  const trimmed = value.trim();
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!trimmed || busy) return;
        onSubmit(trimmed);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          onCancel();
        }
      }}
      className="flex items-center gap-1 px-2 py-1"
      style={{ paddingLeft: `${depth * 12 + 8}px` }}
    >
      <input
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={placeholder ?? 'Folder name'}
        maxLength={128}
        autoFocus
        className="min-w-0 flex-1 rounded border border-border bg-background px-1.5 py-0.5 text-xs"
      />
      <button
        type="submit"
        disabled={!trimmed || busy}
        className="rounded bg-primary px-2 py-0.5 text-xs text-primary-foreground disabled:opacity-50"
      >
        {busy ? '…' : 'OK'}
      </button>
      <button type="button" onClick={onCancel} className="text-xs text-muted-foreground underline">
        Cancel
      </button>
    </form>
  );
}

interface TreeRowProps {
  icon: React.ReactNode;
  label: string;
  protectedFolder?: boolean;
  /**
   * Sparse holder-wide Fias Transfer exposure map. Passed down rather than
   * fetched per row: the tree can hold hundreds of nodes and the badge is
   * usually absent, so one request per holder beats one per folder.
   */
  exposures?: Record<string, number>;
  selected: boolean;
  onClick: () => void;
  depth: number;
  /** Right-aligned slot for hover actions. Only user-managed folders
   * pass anything here — protected folders keep the lock badge. */
  actions?: React.ReactNode;
  /** When provided, the row becomes a drop target. Fires with the
   * documentId carried in `dataTransfer`. */
  onDropDocument?: (documentId: string) => void;
  /** The folder this row stands for. Present on folder rows only —
   * the virtual "Documents" and "Trash" rows pass nothing. Makes
   * the row a folder drag source (when user-managed) and a folder
   * drop destination. */
  folder?: VaultDocumentFolderDto;
  /** Marks the virtual "Documents" row as the Holder-root drop
   * destination, so a nested folder can be dragged back out to the
   * top level. Trash sets neither this nor `folder` and stays inert. */
  rootDropTarget?: boolean;
}

function TreeRow({
  icon,
  label,
  protectedFolder,
  exposures,
  selected,
  onClick,
  depth,
  actions,
  onDropDocument,
  folder,
  rootDropTarget,
}: TreeRowProps) {
  const { dropHover, isDragSource, isDraggable, dndProps } = useFolderRowDnd({
    // A folder row targets itself; "Documents" targets the Holder
    // root so a nested folder can be dragged back to the top level.
    dropTarget: folder ? folder.folderId : rootDropTarget ? null : undefined,
    dragFolder: folder,
    onDropDocument,
  });

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      {...dndProps}
      className={`group flex items-center gap-2 rounded px-2 py-1 text-left text-sm transition ${
        selected
          ? 'bg-primary/10 font-medium text-foreground'
          : 'text-muted-foreground hover:bg-muted/40'
      } ${dropHover ? 'ring-2 ring-primary/60 bg-primary/5' : ''} ${
        isDragSource ? 'opacity-50' : ''
      } ${isDraggable ? 'cursor-grab active:cursor-grabbing' : ''}`}
      style={{ paddingLeft: `${depth * 12 + 8}px` }}
    >
      {icon}
      <span className="truncate">{label}</span>
      {/* Disclosure first: a folder reachable from outside is the one thing
          here the user needs to see WITHOUT hovering. Hover actions and the
          system-folder lock come after. */}
      {folder && <FolderExposureBadge folderId={folder.folderId} exposures={exposures} />}
      {protectedFolder ? (
        <Lock className="ml-auto h-3 w-3 text-muted-foreground" aria-label="System folder" />
      ) : (
        actions
      )}
    </div>
  );
}
